import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import {
  readListState,
  writeListState,
  type ListFilters,
  type ListState,
} from '@/lib/task-list-url';

/**
 * LISTE DURUMU <-> ADRES CUBUGU koprusu.
 *
 * Liste ekranlari (taleplerim, piyasa, tekliflerim, talep detayi) ayni
 * kapiyi kullanir. Ayri ayri yazilsalardi "filtre degisince sayfayi sifirla"
 * kurali birinde unutulur ve kullanici ikinci sayfada bos bir liste gorurdu.
 *
 * `replace: true`: filtre ve sayfa degisimleri gecmis yiginina KAYIT BIRAKMAZ
 * (aksi halde geri tusu kirk kez basilmadan listeden cikilamazdi). Sekme
 * degisimi de ayni davraniyor — o da ayni sayfanin bir gorunumu.
 */
export type UrlListApi = ListState & {
  setTab: (tab: string) => void;
  setOffset: (offset: number) => void;
  setFilters: (filters: ListFilters) => void;
  /** Parametreler degisti; ilk sayfaya don. */
  resetOffset: () => void;
};

export const useUrlList = (defaultTab: string): UrlListApi => {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(
    () => readListState(searchParams, defaultTab),
    [searchParams, defaultTab],
  );

  const apply = useCallback(
    (next: ListState) => {
      setSearchParams(writeListState(next, defaultTab), { replace: true });
    },
    [setSearchParams, defaultTab],
  );

  const setTab = useCallback(
    (tab: string) => {
      // Sekme degisimi listeyi bastan kurar; eski ofset yeni sekmede anlamsiz.
      apply({ ...state, tab, offset: 0 });
    },
    [apply, state],
  );

  const setOffset = useCallback(
    (offset: number) => {
      apply({ ...state, offset });
    },
    [apply, state],
  );

  const setFilters = useCallback(
    (filters: ListFilters) => {
      // FILTRE DEGISINCE SAYFA SIFIRLANIR. Bu satirin olmamasi, "3. sayfadayken
      // filtre uygulayinca bos liste" seklinde gorunen klasik hatadir.
      apply({ ...state, filters, offset: 0 });
    },
    [apply, state],
  );

  const resetOffset = useCallback(() => {
    apply({ ...state, offset: 0 });
  }, [apply, state]);

  return { ...state, setTab, setOffset, setFilters, resetOffset };
};
